import React, { useState, useContext, useEffect } from "react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import "../adminModals.css";
import "./shopContactModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import PhoneInput from "../../common/phoneInput/phoneInput";
import { Mail } from "lucide-react";
import useModalDismiss from "../../common/useModalDismiss";

/* The same glyphs the profile card links out with, so the edit surface and
   the display surface read as one thing. */
const SOCIALS = [
	{ key: "website", icon: "https://cdn-icons-png.flaticon.com/512/545/545674.png", labelKey: "website", placeholder: "https://" },
	{ key: "instagram", icon: "https://cdn-icons-png.flaticon.com/512/1384/1384031.png", labelKey: "instagram", placeholder: "https://instagram.com/" },
	{ key: "facebook", icon: "https://cdn-icons-png.flaticon.com/512/1384/1384005.png", labelKey: "facebook", placeholder: "https://facebook.com/" },
	{ key: "tiktok", icon: "https://cdn-icons-png.flaticon.com/512/3046/3046120.png", labelKey: "tiktok", placeholder: "https://tiktok.com/@" },
];

const ShopContactModal = ({ open, onClose, shopData, setShopData }) => {
	useModalDismiss(onClose, { enabled: open });
	const { language, setUserData, userData } = useContext(CombinedContext);

	const [localData, setLocalData] = useState({
		phone: "",
		email: "",
		website: "",
		instagram: "",
		facebook: "",
		tiktok: "",
	});

	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (shopData) {
			setLocalData({
				phone: shopData.phone || "",
				email: shopData.email || "",
				website: shopData.socialLinks?.website || "",
				instagram: shopData.socialLinks?.instagram || "",
				facebook: shopData.socialLinks?.facebook || "",
				tiktok: shopData.socialLinks?.tiktok || "",
			});
		}
	}, [shopData]);

	if (!open) return null;

	const t = translations[language].profile;
	const contactT = t.sidebar.contact;

	const handleSave = async () => {
		const updateData = {
			phone: localData.phone,
			email: localData.email,
			socialLinks: {
				website: localData.website,
				instagram: localData.instagram,
				facebook: localData.facebook,
				tiktok: localData.tiktok,
			},
		};

		// ✅ Send to backend
		try {
			setSaving(true);
			const res = await companyBasedRequest.updateShopContactInfo(shopData.shopId, updateData, userData.cognitoTokens.AccessToken);
			setUserData((prev) => ({ ...prev, shop: res.shop }));
			setShopData((prev) => ({
				...prev,
				phone: updateData.phone,
				email: updateData.email,
				socialLinks: updateData.socialLinks,
			}));
		} catch (err) {
			console.error("Failed to update shop contact info:", err);
		} finally {
			setSaving(false);
		}

		onClose();
	};

	return (
		<div className="sf-pm-overlay">
			<div className="sf-pm-modal">
				{/* HEADER */}
				<div className="sf-pm-header">
					<div className="sf-pm-heading">
						<h2 className="sf-pm-title">{contactT.title}</h2>
						<p className="sf-pm-sub">{t.modal.contact.description}</p>
					</div>
					<button className="sf-pm-close" onClick={onClose} aria-label={t.actions.cancel}>
						✕
					</button>
				</div>

				{/* BODY */}
				<div className="sf-pm-body">
					{/* CONTACT SECTION */}
					<section className="sf-pm-section">
						<p className="sf-pm-label">{contactT.title}</p>

						<div className="sf-pm-card">
							<div className="sf-pm-field">
								<label className="sf-pm-field-label">{contactT.phone}</label>
								<PhoneInput businessPhone={localData.phone} setBusinessPhone={(val) => setLocalData({ ...localData, phone: val })} />
							</div>

							<div className="sf-pm-field">
								<label className="sf-pm-field-label" htmlFor="sf-pm-contact-email">
									{contactT.email}
								</label>
								<div className="sf-pm-input-icon">
									<Mail size={17} strokeWidth={1.8} />
									<input
										id="sf-pm-contact-email"
										className="sf-pm-input"
										type="email"
										inputMode="email"
										autoComplete="email"
										placeholder={contactT.email}
										value={localData.email}
										onChange={(e) => setLocalData({ ...localData, email: e.target.value })}
									/>
								</div>
							</div>
						</div>
					</section>

					{/* SOCIAL SECTION */}
					<section className="sf-pm-section">
						<p className="sf-pm-label">{contactT.socialTitle}</p>

						<div className="sf-pm-card">
							{SOCIALS.map(({ key, icon, labelKey, placeholder }) => (
								<div className="sf-pm-field" key={key}>
									<label className="sf-pm-field-label" htmlFor={`sf-pm-social-${key}`}>
										{contactT[labelKey]}
									</label>
									<div className="sf-pm-input-icon sf-pm-social-icon">
										<img src={icon} alt="" aria-hidden="true" />
										<input
											id={`sf-pm-social-${key}`}
											className="sf-pm-input"
											type="text"
											inputMode="url"
											autoCapitalize="none"
											autoCorrect="off"
											spellCheck="false"
											placeholder={placeholder}
											value={localData[key]}
											onChange={(e) => setLocalData({ ...localData, [key]: e.target.value })}
										/>
									</div>
								</div>
							))}
						</div>
					</section>
				</div>

				{/* FOOTER */}
				<div className="sf-pm-footer">
					<button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
						{saving && <span className="sf-pm-spinner" aria-hidden="true" />}
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ShopContactModal;
